/*
 * CUSTOMIZED · HYPOGEN Lieferzeitanzeige · Controller, Version 8
 * Zieldatei: assets/hypogen-delivery-controller.js
 * Einbindung: Lieferblock in beiden Produktvorlagen, nach Kalender
 * und Berechnung. Benötigt Anzeigesnippet und Konfiguration Version 3.
 * Layoutanbindung: hypogen-set-advantage-note.liquid.
 *
 * Lieferlandwahl ist ausschließlich eine Schätz-Präferenz.
 * Keine Lokalisierungs-, Formular-, Warenkorb- oder Checkout-Anfrage.
 * Nur eine ausdrücklich gewählte Länderkennung wird im sessionStorage
 * dieses Tabs gespeichert, nach Theme getrennt. Keine Personenkennung,
 * kein Tracking, kein Cookie. Bei gesperrtem Speicher nur Seitenwahl.
 *
 * Verkaufsfreigabe folgt variant.available und der Mengenfeldprüfung.
 * Firmenausblendung folgt dem übergebenen aktuellen Firmenkauf-Kontext.
 * Sichtbare Abstände statt bloßer Wrapper-Abstände; die echte
 * Smartphone-Abnahme ist durch isolierte Logiktests nicht ersetzt.
 */
(() => {
  'use strict';
  if (customElements.get('hypogen-delivery-indicator')) return;

  class HypogenDeliveryIndicator extends HTMLElement {
    connectedCallback() {
      if (this.active) return;
      this.active = true;
      this.hidden = true;
      this.pendingVariant = false;
      this.cleanups = [];
      this.scope = this.closest('product-info');
      this.container = this.closest('.product__info-container');
      try {
        this.config = JSON.parse(this.querySelector('[data-hypogen-delivery-config]').textContent);
        this.variants = JSON.parse(this.querySelector('[data-hgn-delivery-variants]').textContent);
        this.select = this.querySelector('[data-hgn-delivery-country]');
        this.label = this.querySelector('[data-hgn-delivery-country-label]');
        if (!this.scope || !this.container || !this.select || !this.label || !Array.isArray(this.variants)) {
          throw new Error('Missing delivery context');
        }
        for (const option of [...this.select.options]) {
          if (!this.isConfiguredCountry(option.value)) option.remove();
        }
        let country = this.dataset.country;
        try {
          const saved = sessionStorage.getItem(this.dataset.storageKey);
          if (saved && this.isSelectableCountry(saved)) country = saved;
        } catch (_) {}
        this.setCountry(country);
      } catch (_) {
        return;
      }

      this.onChange = (event) => {
        if (event.target === this.select) {
          event.stopPropagation();
          if (!this.isSelectableCountry(this.select.value)) return;
          this.setCountry(this.select.value);
          try {
            sessionStorage.setItem(this.dataset.storageKey, this.country);
          } catch (_) {}
          window.dispatchEvent(new CustomEvent('hgn:delivery-country', {
            detail: { key: this.dataset.storageKey, country: this.country }
          }));
          this.renderEstimate();
          return;
        }
        if (event.target.matches('input[name="id"]')) {
          this.pendingVariant = false;
          this.renderEstimate();
        } else if (event.target.matches('input[name="quantity"]')) {
          this.renderEstimate();
        }
      };
      this.onInput = (event) => {
        if (event.target.matches('input[name="quantity"]')) this.renderEstimate();
      };
      this.scope.addEventListener('change', this.onChange);
      this.scope.addEventListener('input', this.onInput);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.cleanups.push(subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, ({ data }) => {
          if (data?.target && this.scope.contains(data.target)) {
            this.pendingVariant = true;
            this.hidden = true;
            this.queueLayout();
          }
        }));
        this.cleanups.push(subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (String(data?.sectionId) !== this.dataset.sectionId) return;
          this.pendingVariant = !data?.variant;
          this.renderEstimate();
        }));
      }
      this.onCountrySync = (event) => {
        if (event.detail?.key !== this.dataset.storageKey) return;
        if (!this.isSelectableCountry(event.detail.country)) return;
        if (this.country === event.detail.country) return;
        this.setCountry(event.detail.country);
        this.renderEstimate();
      };
      this.onResume = () => {
        if (document.visibilityState === 'hidden') return;
        try {
          const saved = sessionStorage.getItem(this.dataset.storageKey);
          if (saved && this.isSelectableCountry(saved)) this.setCountry(saved);
        } catch (_) {}
        this.renderEstimate();
      };
      this.onResize = () => this.queueLayout();
      window.addEventListener('hgn:delivery-country', this.onCountrySync);
      document.addEventListener('visibilitychange', this.onResume);
      window.addEventListener('focus', this.onResume);
      window.addEventListener('pageshow', this.onResume);
      window.addEventListener('resize', this.onResize);
      if (typeof ResizeObserver === 'function') {
        this.resizeObserver = new ResizeObserver(this.onResize);
        this.resizeObserver.observe(this.container);
      }
      if (typeof MutationObserver === 'function') {
        this.mutationObserver = new MutationObserver(this.onResize);
        this.mutationObserver.observe(this.container, {
          childList: true, subtree: true, attributes: true,
          attributeFilter: ['hidden']
        });
      }
      document.fonts?.ready.then(() => {
        if (this.active) this.queueLayout();
      });
      this.renderEstimate();
      this.scheduleRefresh();
    }

    isConfiguredCountry(country) {
      if (!/^[A-Z]{2}$/.test(country || '')) return false;
      const groups = this.config?.transit?.groups;
      if (!Array.isArray(groups)) return false;
      return groups.filter((group) => (
        Array.isArray(group?.countries) && group.countries.includes(country)
      )).length === 1;
    }

    isSelectableCountry(country) {
      return this.isConfiguredCountry(country) && [...this.select.options].some(
        (option) => option.value === country
      );
    }

    setCountry(country) {
      this.country = String(country || '').trim().toUpperCase();
      this.select.value = this.isSelectableCountry(this.country) ? this.country : '';
      this.label.textContent = `${this.country} ▾:`;
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      cancelAnimationFrame(this.layoutFrame);
      this.resizeObserver?.disconnect();
      this.mutationObserver?.disconnect();
      this.cleanups?.forEach((unsubscribe) => unsubscribe());
      if (this.scope && this.onChange) this.scope.removeEventListener('change', this.onChange);
      if (this.scope && this.onInput) this.scope.removeEventListener('input', this.onInput);
      if (this.onResume) {
        document.removeEventListener('visibilitychange', this.onResume);
        window.removeEventListener('focus', this.onResume);
        window.removeEventListener('pageshow', this.onResume);
      }
      if (this.onResize) window.removeEventListener('resize', this.onResize);
      if (this.onCountrySync) window.removeEventListener('hgn:delivery-country', this.onCountrySync);
      this.resetLayout();
      this.active = false;
    }

    scheduleRefresh() {
      clearTimeout(this.timer);
      this.timer = setTimeout(() => {
        if (!this.isConnected) return;
        this.renderEstimate();
        this.scheduleRefresh();
      }, 60000 - (Date.now() % 60000) + 25);
    }

    queueLayout() {
      cancelAnimationFrame(this.layoutFrame);
      this.layoutFrame = requestAnimationFrame(() => {
        if (this.active && this.isConnected) this.syncLayout();
      });
    }

    resetLayout() {
      this.layoutEnd?.removeAttribute('data-hgn-delivery-gap-end');
      this.layoutStart?.removeAttribute('data-hgn-delivery-buy-start');
      this.layoutVariants?.removeAttribute('data-hgn-delivery-variants-layout');
      this.layoutRow?.removeAttribute('data-hgn-delivery-row-layout');
      this.layoutRow?.style.removeProperty('--hgn-delivery-row-margin');
      this.layoutEnd = null;
      this.layoutStart = null;
      this.layoutVariants = null;
      this.layoutRow = null;
      this.container?.classList.remove('hgn-delivery-layout-ready');
      this.container?.classList.remove('hgn-delivery-layout-no-advantage');
      this.container?.style.removeProperty('--hgn-delivery-atc-margin');
    }

    textBounds(element) {
      const doc = element?.ownerDocument;
      if (!doc) return null;
      const walker = doc.createTreeWalker(element, 4);
      const range = doc.createRange();
      let top = Infinity;
      let bottom = -Infinity;
      let node;
      while ((node = walker.nextNode())) {
        if (!node.textContent.trim()) continue;
        range.selectNodeContents(node);
        for (const rect of range.getClientRects()) {
          if (rect.width <= 0 || rect.height <= 0) continue;
          top = Math.min(top, rect.top);
          bottom = Math.max(bottom, rect.bottom);
        }
      }
      return Number.isFinite(top + bottom) ? { top, bottom } : null;
    }

    mobileParagraphGap(description, end) {
      if (!description || !end) return null;
      const paragraphs = [...description.querySelectorAll('p')];
      const endText = this.textBounds(end);
      if (!endText) return null;
      for (let index = 1; index < paragraphs.length; index += 1) {
        const before = this.textBounds(paragraphs[index - 1]);
        const after = this.textBounds(paragraphs[index]);
        if (!before || !after) continue;
        const gap = after.top - before.bottom;
        if (Number.isFinite(gap) && gap > 0) {
          return { gap, endBottom: endText.bottom };
        }
      }
      return null;
    }

    layoutMargin(current, actualGap, targetGap) {
      if (![current, actualGap, targetGap].every(Number.isFinite) || targetGap < 0) return null;
      return Math.round((current + targetGap - actualGap) * 100) / 100;
    }

    syncLayout() {
      if (!this.container) return;
      const visible = (element) => Boolean(
        element && !element.hidden &&
        getComputedStyle(element).display !== 'none' &&
        element.getBoundingClientRect().height > 0
      );
      const children = [...this.container.children];
      const advantage = children.find((element) => (
        element.matches('.hypogen-set-advantage-note') && visible(element)
      ));
      const variants = children.find((element) => (
        element.matches('variant-selects') && visible(element)
      ));
      const row = children.find((element) => (
        element.matches('.pdp-qty-atc-row') && visible(element)
      ));
      const end = advantage || (visible(this) ? this : null);
      const start = variants || row;
      const ordered = start && (!end || children.indexOf(start) > children.indexOf(end));
      const add = row?.querySelector('.product-form__submit');
      const quantity = row?.querySelector('quantity-input.quantity');
      if (!ordered || !row || !visible(add)) {
        this.resetLayout();
        return;
      }
      if (this.layoutEnd !== end || this.layoutStart !== start ||
          this.layoutVariants !== variants || this.layoutRow !== row) {
        this.resetLayout();
        this.layoutEnd = end;
        this.layoutStart = start;
        this.layoutVariants = variants;
        this.layoutRow = row;
      }
      if (end) {
        end.setAttribute('data-hgn-delivery-gap-end', '');
        start.setAttribute('data-hgn-delivery-buy-start', '');
      } else {
        start.removeAttribute('data-hgn-delivery-buy-start');
      }
      variants?.setAttribute('data-hgn-delivery-variants-layout', '');
      row.setAttribute('data-hgn-delivery-row-layout', '');

      // Measure actual controls, not the enclosing product-form box.
      const addRect = add.getBoundingClientRect();
      const quantityRect = visible(quantity) ? quantity.getBoundingClientRect() : addRect;
      const rowTop = Math.min(addRect.top, quantityRect.top);
      const rowBottom = Math.max(addRect.bottom, quantityRect.bottom);
      let referenceBottom;
      let targetGap;
      if (variants) {
        const controls = [...variants.querySelectorAll(
          '.product-form__input input[type="radio"] + label, ' +
          '.product-form__input select, .swatch-input__label'
        )].filter(visible);
        const direct = this.container.querySelector('[data-direct-checkout]');
        if (!controls.length || !visible(direct)) {
          this.resetLayout();
          return;
        }
        referenceBottom = Math.max(...controls.map((element) => (
          element.getBoundingClientRect().bottom
        )));
        targetGap = direct.getBoundingClientRect().top - rowBottom;
      } else if (end) {
        referenceBottom = end.getBoundingClientRect().bottom;
        const description = this.container.querySelector('.product__description');
        const paragraphs = description ? [...description.querySelectorAll('p')] : [];
        const paragraphMargins = paragraphs.filter(visible).map((paragraph) => (
          parseFloat(getComputedStyle(paragraph).marginBottom)
        )).filter((margin) => Number.isFinite(margin) && margin > 0);
        targetGap = paragraphMargins[0] || parseFloat(
          getComputedStyle(description || this.container).fontSize
        );
        // Use the same measured paragraph reference for sets at every width.
        // Compare text fragments with text fragments, not line boxes.
        if (advantage) {
          const measured = this.mobileParagraphGap(description, advantage);
          if (measured && measured.endBottom + measured.gap > referenceBottom + targetGap) {
            referenceBottom = measured.endBottom;
            targetGap = measured.gap;
          }
        }
      } else {
        this.resetLayout();
        return;
      }
      const currentMargin = parseFloat(getComputedStyle(row).marginTop);
      const margin = this.layoutMargin(currentMargin, rowTop - referenceBottom, targetGap);
      if (margin === null) {
        this.resetLayout();
        return;
      }
      const value = `${margin}px`;
      if (row.style.getPropertyValue('--hgn-delivery-row-margin') !== value) {
        row.style.setProperty('--hgn-delivery-row-margin', value);
      }
    }

    renderEstimate() {
      this.hidden = true;
      try {
        if (!this.config?.estimateEnabled || this.pendingVariant) return;
        if (this.config.hideForCompanyPurchase && this.dataset.companyPurchase === 'true') return;
        if (!this.isSelectableCountry(this.country)) return;
        const form = document.getElementById(`product-form-${this.dataset.sectionId}`);
        const idInput = form?.querySelector('input[name="id"]');
        if (!idInput || idInput.disabled || !idInput.value) return;
        const variant = this.variants.find((item) => String(item.id) === String(idInput.value));
        const quantityInput = this.scope.querySelector('input[name="quantity"]');
        const quantity = quantityInput ? Number(quantityInput.value) : 1;
        if (!Number.isInteger(quantity) || quantity < 1) return;
        if (variant?.available !== true) return;
        if (quantityInput && !quantityInput.checkValidity()) return;
        const estimate = globalThis.HypogenDeliveryEstimate?.calculate(this.config, this.country);
        if (!estimate || estimate.minWeekdays < 1) return;
        let text = this.dataset.estimateText || '';
        if (!text.includes('[min]') || !text.includes('[max]')) return;
        const numberFormat = new Intl.NumberFormat(document.documentElement.lang || 'de');
        const min = numberFormat.format(estimate.minWeekdays);
        const max = numberFormat.format(estimate.maxWeekdays);
        if (min === max) text = text.replace('[min]–[max]', '[min]').replace('[min]-[max]', '[min]');
        text = text.split('[min]').join(min).split('[max]').join(max);
        const output = this.querySelector('[data-hgn-delivery-estimate]');
        if (!output) return;
        output.textContent = text;
        this.hidden = false;
      } catch (_) {
        this.hidden = true;
      } finally {
        this.queueLayout();
      }
    }
  }
  customElements.define('hypogen-delivery-indicator', HypogenDeliveryIndicator);
})();
